import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useState, useCallback, useMemo, useEffect } from "react"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  SendIcon,
} from "lucide-react"
import { PDFDocument } from "pdf-lib"
import { usePage, router } from "@inertiajs/react"

import type { CoversheetData } from "@/components/coversheet-document"
import type {
  SendFaxProps,
  CoversheetTemplate,
  Contact,
  FaxNumber,
} from "./types"

import { getDefaultCoversheet, isTemplateComplete } from "./helpers"
import { StepIndicator } from "./step-indicator"
import { RecipientPicker } from "./recipient-picker"
import { FromSelect } from "./from-select"
import { FileDropZone } from "./file-drop-zone"
import { CoversheetConfig } from "./coversheet-config"
import { CoversheetBuilderStep } from "./coversheet-builder-step"
import { ReviewStep } from "./review-step"

type PageProps = {
  numbers: FaxNumber[]
  contacts: Contact[]
  prev_rec_numbers?: string[]
  prev_sent_numbers?: string[]
  templates?: CoversheetTemplate[]
}

export function SendFax({
  dialogOpen,
  setDialogOpen,
  onCreateTemplate,
  onSend,
}: SendFaxProps) {

  // Initialize all data as state from server props
  const [numbers, setNumbers] = useState<FaxNumber[]>([])
  const [contacts, setContacts] = useState<Contact[]>([])
  const [prevRecNumbers, setPrevRecNumbers] = useState<string[]>([])
  const [prevSentNumbers, setPrevSentNumbers] = useState<string[]>([])
  const [templates, setTemplates] = useState<CoversheetTemplate[]>([])


  useEffect(() => {
    let cancelled = false

    async function loadSendFaxDetails() {
      console.log("Initializing Send Fax dialog data...")

      const res = await fetch("/faxes/send_fax_details", {
        headers: { Accept: "application/json" },
      })

      if (!res.ok) {
        throw new Error(`Failed to fetch: ${res.status}`)
      }

      const send_fax_details = await res.json()

      if (cancelled) return

      console.log("send_fax_details:", send_fax_details)

      setNumbers(send_fax_details.numbers ?? [])
      setContacts(send_fax_details.contacts ?? [])
      setPrevRecNumbers(send_fax_details.prev_rec_numbers ?? [])
      setPrevSentNumbers(send_fax_details.prev_sent_numbers ?? [])
      setTemplates(send_fax_details.templates ?? [])
    }

    loadSendFaxDetails()

    return () => {
      cancelled = true
    }
  }, [])

  const [recipientPickerOpen, setRecipientPickerOpen] = useState(false)
  const [step, setStep] = useState(0)

  const [toNumbers, setToNumbers] = useState<string[]>([])
  const [fromNumber, setFromNumber] = useState("")
  const [files, setFiles] = useState<File[]>([])

  const getNumberName = useCallback(
    (num: string) => numbers.find((n) => n.number === num)?.name ?? num,
    [numbers]
  )

  const [includeCoversheet, setIncludeCoversheet] = useState(true)
  const [coversheetMode, setCoversheetMode] =
    useState<"build" | "upload">("build")
  const [coversheetFile, setCoversheetFile] = useState<File | null>(null)

  const [activeTemplateId, setActiveTemplateId] =
    useState<string | null>(null)

  const [inlineSubject, setInlineSubject] = useState("")
  const [inlineMessage, setInlineMessage] = useState("")

  const [coversheet, setCoversheet] =
    useState<CoversheetData>(getDefaultCoversheet())

  const [previewRecipientIndex, setPreviewRecipientIndex] =
    useState(0)

  const activeTemplate = useMemo(
    () => templates.find((t) => t.id === activeTemplateId) ?? null,
    [templates, activeTemplateId]
  )

  const templateSkipsBuilder = activeTemplate
    ? isTemplateComplete(activeTemplate)
    : false

  const calculatedDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  )

  const [calculatedPages, setCalculatedPages] = useState("0")

  // ── Partial Reload On Open ──

  useEffect(() => {
    if (!dialogOpen) return

    const controller = new AbortController()

    fetch("/faxes/send_fax_details", {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        setPrevRecNumbers(data.prev_rec_numbers ?? [])
        setPrevSentNumbers(data.prev_sent_numbers ?? [])
        setTemplates(data.templates ?? [])
      })
      .catch((e) => {
        if (e.name !== "AbortError") console.error(e)
      })

    return () => controller.abort()
  }, [dialogOpen])

  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      console.log("Reloading Send Fax dialog data...")
      router.reload({
        only: ["numbers", "contacts", "prev_rec_numbers", "prev_sent_numbers", "templates"],
      })
    } else {
      setStep(0)
      setToNumbers([])
      setFromNumber("")
      setFiles([])
      setIncludeCoversheet(true)
      setCoversheetMode("build")
      setCoversheetFile(null)
      setCoversheet(getDefaultCoversheet())
      setActiveTemplateId(null)
      setPreviewRecipientIndex(0)
      setInlineSubject("")
      setInlineMessage("")
    }

    setDialogOpen(open)
  }, [setDialogOpen])

  // ── Count Pages ──
  useEffect(() => {
    let cancelled = false

    async function countPages() {
      let total = includeCoversheet ? 1 : 0

      for (const file of files) {
        if (
          file.type === "application/pdf" ||
          file.name.toLowerCase().endsWith(".pdf")
        ) {
          try {
            const bytes = await file.arrayBuffer()
            const doc = await PDFDocument.load(bytes, {
              ignoreEncryption: true,
            })
            total += doc.getPageCount()
          } catch {
            total += 1
          }
        } else {
          total += 1
        }
      }

      if (!cancelled) setCalculatedPages(String(total))
    }

    countPages()
    return () => {
      cancelled = true
    }
  }, [files, includeCoversheet])

  useEffect(() => {
    setCoversheet((prev) => ({
      ...prev,
      date: calculatedDate,
      pages: calculatedPages,
    }))
  }, [calculatedDate, calculatedPages])

  const canProceedStep0 =
    toNumbers.length > 0 && fromNumber && files.length > 0

  const skipBuilder =
    !includeCoversheet ||
    coversheetMode === "upload" ||
    (coversheetMode === "build" && templateSkipsBuilder)

  const showsCoversheetStep =
    includeCoversheet &&
    coversheetMode === "build" &&
    !templateSkipsBuilder

  const goNext = () => {
    if (step === 0) {
      if (skipBuilder) setStep(2)
      else setStep(1)
    } else {
      setStep((s) => Math.min(s + 1, 2))
    }
  }

  const goBack = () => {
    if (step === 2 && skipBuilder) setStep(0)
    else setStep((s) => Math.max(s - 1, 0))
  }

  const handleSend = () => {
    if (!onSend) return

    onSend({
      to: toNumbers,
      from: fromNumber,
      files,
      coversheet:
        includeCoversheet && coversheetMode === "build"
          ? coversheet
          : null,
      coversheetFile:
        includeCoversheet && coversheetMode === "upload"
          ? coversheetFile
          : null,
    })

    handleOpenChange(false)
  }

  const updateCoversheet = (
    field: keyof CoversheetData,
    value: string
  ) => {
    setCoversheet((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[92vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Send a Fax</DialogTitle>
          <DialogDescription>
            {step === 0 &&
              "Set the recipients, sender, and attach your documents."}
            {step === 1 &&
              "Customize your cover sheet. Recipient info is auto-filled."}
            {step === 2 && "Review everything before sending."}
          </DialogDescription>

          <StepIndicator
            step={step}
            showsCoversheetStep={showsCoversheetStep}
          />
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {step === 0 && (
            <div className="space-y-5">
              <RecipientPicker
                toNumbers={toNumbers}
                setToNumbers={setToNumbers}
                contacts={contacts}
                prevRecNumbers={prevRecNumbers}
                prevSentNumbers={prevSentNumbers}
                popoverOpen={recipientPickerOpen}
                setPopoverOpen={setRecipientPickerOpen}
              />

              <FromSelect
                numbers={numbers}
                value={fromNumber}
                onChange={setFromNumber}
              />

              <FileDropZone
                files={files}
                setFiles={setFiles}
                disabled={recipientPickerOpen}
              />

              <CoversheetConfig
                includeCoversheet={includeCoversheet}
                setIncludeCoversheet={setIncludeCoversheet}
                coversheetMode={coversheetMode}
                setCoversheetMode={setCoversheetMode}
                coversheetFile={coversheetFile}
                setCoversheetFile={setCoversheetFile}
                templates={templates}
                activeTemplateId={activeTemplateId}
                setActiveTemplateId={setActiveTemplateId}
                onCreateTemplate={onCreateTemplate}
                templateSkipsBuilder={templateSkipsBuilder}
                activeTemplate={activeTemplate}
                inlineSubject={inlineSubject}
                setInlineSubject={setInlineSubject}
                inlineMessage={inlineMessage}
                setInlineMessage={setInlineMessage}
              />
            </div>
          )}

          {step === 1 && (
            <CoversheetBuilderStep
              coversheet={coversheet}
              updateCoversheet={updateCoversheet}
              setCoversheet={setCoversheet}
              toNumbers={toNumbers}
              previewRecipientIndex={previewRecipientIndex}
              setPreviewRecipientIndex={setPreviewRecipientIndex}
              calculatedDate={calculatedDate}
              calculatedPages={calculatedPages}
            />
          )}

          {step === 2 && (
            <ReviewStep
              toNumbers={toNumbers}
              fromNumber={fromNumber}
              getNumberName={getNumberName}
              contacts={contacts}
              files={files}
              includeCoversheet={includeCoversheet}
              coversheetMode={coversheetMode}
              coversheetFile={coversheetFile}
              previewCoversheet={coversheet}
              coversheet={coversheet}
              templateSkipsBuilder={templateSkipsBuilder}
              inlineSubject={inlineSubject}
              inlineMessage={inlineMessage}
              calculatedDate={calculatedDate}
              calculatedPages={calculatedPages}
              activeTemplate={activeTemplate}
            />
          )}
        </div>

        <DialogFooter className="flex justify-between">
          {step > 0 && (
            <Button variant="ghost" onClick={goBack}>
              <ChevronLeftIcon className="size-4 mr-1" />
              Back
            </Button>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>

            {step < 2 ? (
              <Button
                disabled={step === 0 && !canProceedStep0}
                onClick={goNext}
              >
                {step === 0 && skipBuilder ? "Review" : "Next"}
                <ChevronRightIcon className="size-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSend}>
                <SendIcon className="size-4 mr-1.5" />
                Send Fax
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
